import {
  OrderResultsReducerChangeLastOrderAction,
  OrderResultsReducerChangeLastPreviewOrderAction,
  OrderResultsReducerNewOrderPendingAction,
} from 'components/features/rewards/rewardsPane/store/actions/ordersResults'
import { EMPTY, merge, Observable, of } from 'rxjs'
import { mergeMap } from 'rxjs/operators'
import { OrderFailedResult, OrderResultWithPendingStatus, OrderSuccessResultWithMeta } from 'components/features/rewards/rewardsPane/types/orderResult'
import {
  CRYPTO_REWARDS_SELL_MODE_STEP_SELL_AMOUNT,
  CRYPTO_REWARDS_SELL_MODE_STEP_PENDING,
  CRYPTO_REWARDS_SELL_MODE_STEP_PREVIEW_ORDER,
  CRYPTO_REWARDS_SELL_MODE_STEP_SUCCESS,
} from 'components/features/rewards/rewardsPane/constants/paneSteps'
import { EpicRefined } from 'interfaces/middleware'
import { ajaxCatch, AjaxCatchExtraActions } from 'rxOperators/ajaxCatch'
import { CryptoRewardsPaneActions } from 'components/features/rewards/rewardsPane/store/actions'
import {
  createRewardsPaneActionChangeSellModeStepState,
  SellCryptoAction,
  SuccessCryptoRewardsPaneModeStepPayload,
} from 'components/features/rewards/rewardsPane/store/actions/paneState'
import { FLOATING_PANE_CRYPTO_REWARDS_SELL_MODE } from 'components/features/rewards/rewardsPane/constants/paneState'
import { POST_ORDER_SELL_CRYPTO_URL } from 'components/features/rewards/rewardsPane/constants/endpoints'
import { ModifyFloatingPane } from 'actions/floatingPanesActions'
import { CryptoRewardsSellCryptoOrderRequest } from 'components/features/rewards/rewardsPane/types/apiRequests'
import { CryptoRewardsBuyCryptoOrderFailedResponse, CryptoRewardsSellCryptoOrderSuccessResponse } from 'components/features/rewards/rewardsPane/types/apiResponses'
import { PreviewOrderResultWithStatus } from 'components/features/rewards/rewardsPane/types/orderPreviewResult'
import { CRYPTO_REWARDS_PANE_GENERAL_UNKNOWN_RESPONSE_ERROR } from 'components/features/rewards/rewardsPane/constants/defaults'
import { SetRewardSummaryAction } from 'actions/bountyActions'
import size from 'lodash/size'
import { mapOrderDetailsAPIResponseFormatToReducerFormat } from 'components/features/rewards/rewardsPane/store/reducers/ordersResults'
import { filterRequest } from 'rxOperators/filterRequestOfType'
import { currentPageSelector } from 'selectors/globalSelectors'
import { activateAnchorActionCreator, pushActionCreator } from 'actions/navigationActions'
import { REWARDS_PAGE } from 'constants/pages'

type PaneSellMode = typeof FLOATING_PANE_CRYPTO_REWARDS_SELL_MODE

export const createSellCryptoOrder: EpicRefined<CryptoRewardsPaneActions | ModifyFloatingPane | SetRewardSummaryAction | AjaxCatchExtraActions> = (
  (action$, store, { post }) => action$
    .pipe(
      filterRequest(SellCryptoAction.type),
      mergeMap(
        (action) => {
          const { payload } = action
          if (payload.params.step !== CRYPTO_REWARDS_SELL_MODE_STEP_PREVIEW_ORDER) {
            return EMPTY
          }

          const { fields }: SuccessCryptoRewardsPaneModeStepPayload<PaneSellMode> = payload.params

          if (!fields) {
            throw new Error('No order data')
          }

          const {
            cryptoCurrencyIDChosenForSelling,
            sellingAmountUSD,
            sellingAmountCrypto,
          } = fields

          const actionOpenSendingOrderStep: ModifyFloatingPane = (
            createRewardsPaneActionChangeSellModeStepState({
              step: CRYPTO_REWARDS_SELL_MODE_STEP_PENDING,
              progressPercents: 70,
              progressLabel: 'Sending order...',
              fields: {
                cryptoCurrencyIDChosenForSelling,
                sellingAmountUSD,
                sellingAmountCrypto,
              },
            })
          )
          const actionOpenSendingOrderStepObservable: Observable<ModifyFloatingPane> = of(actionOpenSendingOrderStep)
          const actionResetLastOrderResultObservable = of(
            new OrderResultsReducerNewOrderPendingAction(FLOATING_PANE_CRYPTO_REWARDS_SELL_MODE)
          )

          const sendOrderParams: CryptoRewardsSellCryptoOrderRequest = {
            amount_usd: sellingAmountUSD,
            amount_crypto: sellingAmountCrypto,
            ticker: cryptoCurrencyIDChosenForSelling,
          }
          const sendOrderObservable = post(POST_ORDER_SELL_CRYPTO_URL, {
            body: sendOrderParams,
          }).pipe(
            mergeMap(({ response }) => {
              const {
                order,
                reward_account,
              }: CryptoRewardsSellCryptoOrderSuccessResponse = response

              const orderSuccessResult: OrderSuccessResultWithMeta<PaneSellMode> = mapOrderDetailsAPIResponseFormatToReducerFormat(order)
              const orderResultWithStatus: OrderResultWithPendingStatus<PaneSellMode> = {
                mode: FLOATING_PANE_CRYPTO_REWARDS_SELL_MODE,
                isPending: false,
                success: orderSuccessResult,
                errors: undefined,
              }
              const actionSetOrderResult = new OrderResultsReducerChangeLastOrderAction(orderResultWithStatus)
              const actionChangeUserRewardsState = new SetRewardSummaryAction(reward_account)
              const actionChangePaneStep = createRewardsPaneActionChangeSellModeStepState({
                step: CRYPTO_REWARDS_SELL_MODE_STEP_SUCCESS,
                progressPercents: 100,
                progressLabel: undefined,
                fields,
              })

              const currentPage = currentPageSelector(store.getState(), { location: window.location })
              const isRewardsPage = currentPage === 'rewards'
              const redirectAction = isRewardsPage ? EMPTY : merge(
                of(pushActionCreator(null)),
                of(pushActionCreator(REWARDS_PAGE)),
                of(activateAnchorActionCreator(REWARDS_PAGE)),
              )

              return merge(
                of(actionSetOrderResult),
                of(actionChangeUserRewardsState),
                of(actionChangePaneStep),
                of(action.success(response)),
                redirectAction,
              )
            }),
            ajaxCatch((error) => {
              const {
                amount_usd,
                ticker,
                non_field_errors,
              }: CryptoRewardsBuyCryptoOrderFailedResponse = Boolean(size(error.response))
                ? error.response
                : CRYPTO_REWARDS_PANE_GENERAL_UNKNOWN_RESPONSE_ERROR
              const fieldsErrors = {
                amount_usd,
                ticker,
              }
              const previewOrderFailedErrors: OrderFailedResult<PaneSellMode> = {
                fieldsErrors: fieldsErrors,
                generalErrors: non_field_errors,
              }
              const previewOrderResultWithStatus: PreviewOrderResultWithStatus = {
                isPending: false,
                errors: previewOrderFailedErrors,
                success: undefined,
              }
              const actionSetOrderResult = new OrderResultsReducerChangeLastPreviewOrderAction(previewOrderResultWithStatus)
              const actionChangePaneStep = createRewardsPaneActionChangeSellModeStepState({
                step: CRYPTO_REWARDS_SELL_MODE_STEP_SELL_AMOUNT,
                progressPercents: 20,
                progressLabel: undefined,
                fields,
              })

              return merge(
                of(actionSetOrderResult),
                of(actionChangePaneStep),
                of(action.fail({ errorCode: error.status, errorsMap: error.response })),
              )
            })
          )
          return merge(
            actionOpenSendingOrderStepObservable,
            actionResetLastOrderResultObservable,
            sendOrderObservable
          )
        }
      )
    )
)
